import { Component } from '@theme/component';
import {
  center,
  closest,
  clamp,
  getVisibleElements,
  mediaQueryLarge,
  prefersReducedMotion,
  viewTransition,
  scheduler,
} from '@theme/utilities';
import { Scroller, scrollIntoView } from '@theme/scrolling';
import { SlideshowSelectEvent } from '@theme/events';

const SLIDE_VISIBLITY_THRESHOLD = 0.7;

/**
 * Slideshow custom element that allows sliding between content.
 *
 * @typedef {Object} Refs
 * @property {HTMLElement} scroller
 * @property {HTMLElement} slideshowContainer
 * @property {HTMLElement[]} [slides]
 * @property {HTMLElement} [current]
 * @property {HTMLElement[]} [thumbnails]
 * @property {HTMLElement[]} [dots]
 * @property {HTMLButtonElement} [previous]
 * @property {HTMLButtonElement} [next]
 *
 * @extends {Component<Refs>}
 */
export class Slideshow extends Component {
  static get observedAttributes() {
    return ['initial-slide'];
  }

  /**
   * @param {string} name
   * @param {string} oldValue
   * @param {string} newValue
   */
  attributeChangedCallback(name, oldValue, newValue) {
    if (name === 'initial-slide' && oldValue !== newValue) {
      queueMicrotask(() => {
        if (!this.isConnected || !this.#scroll || !this.refs.slides) return;

        const index = parseInt(newValue, 10) || 0;
        const slide_id = this.refs.slides[index]?.getAttribute('slide-id');

        if (slide_id) {
          this.select(
            { id: slide_id },
            undefined,
            { animate: false }
          );
        }
      });
    }
  }

  requiredRefs = ['scroller'];

  async connectedCallback() {
    super.connectedCallback();

    if (viewTransition.current) {
      await viewTransition.current;

      if (!this.isConnected) return;
    }

    const { scroller } = this.refs;

    this.#scroll = new Scroller(scroller, {
      onScroll: this.#handleScroll,
      onScrollStart: this.#onTransitionInit,
      onScrollEnd: this.#onTransitionEnd,
    });

    scroller.addEventListener(
      'mousedown',
      this.#handleMouseDown
    );

    /*
     * No celular, o navegador dispara um "mousedown" falso depois de cada toque.
     * Guardamos o horário do último toque para ignorar esse clique de mentira.
     */
    scroller.addEventListener(
      'touchstart',
      this.#markTouch,
      { passive: true }
    );

    /*
     * Impede o navegador de arrastar nativamente
     * imagens e links dentro do carrossel.
     */
    scroller.addEventListener(
      'dragstart',
      this.#preventNativeDrag
    );

    this.addEventListener(
      'mouseenter',
      this.suspend
    );

    this.addEventListener(
      'mouseleave',
      this.resume
    );

    this.addEventListener(
      'pointerenter',
      this.#handlePointerEnter
    );

    document.addEventListener(
      'visibilitychange',
      this.#handleVisibilityChange
    );

    this.#updateControlsVisibility();

    this.disabled =
      this.isNested ||
      this.disabled;

    this.resume();

    this.current =
      this.initialSlideIndex;

    scheduler.schedule(() => {
      let visibleSlidesAmount = 0;

      const initialSlideId =
        this.initialSlide?.getAttribute(
          'slide-id'
        );

      if (
        this.initialSlideIndex !== 0 &&
        initialSlideId
      ) {
        this.select(
          { id: initialSlideId },
          undefined,
          { animate: false }
        );

        visibleSlidesAmount = 1;
      } else {
        visibleSlidesAmount =
          this.#updateVisibleSlides();

        if (visibleSlidesAmount === 0) {
          this.select(
            0,
            undefined,
            { animate: false }
          );

          visibleSlidesAmount = 1;
        }
      }

      this.#resizeObserver =
        new ResizeObserver(async () => {
          if (viewTransition.current) {
            await viewTransition.current;
          }

          if (visibleSlidesAmount > 1) {
            this.#updateVisibleSlides();
          }

          if (
            this.hasAttribute(
              'auto-hide-controls'
            )
          ) {
            this.#updateControlsVisibility();
          }
        });

      this.#resizeObserver.observe(
        this.refs.slideshowContainer
      );
    });
  }

  disconnectedCallback() {
    super.disconnectedCallback();

    const { scroller } = this.refs;

    scroller.removeEventListener(
      'mousedown',
      this.#handleMouseDown
    );

    scroller.removeEventListener(
      'touchstart',
      this.#markTouch
    );

    scroller.removeEventListener(
      'dragstart',
      this.#preventNativeDrag
    );

    this.removeEventListener(
      'mouseenter',
      this.suspend
    );

    this.removeEventListener(
      'mouseleave',
      this.resume
    );

    this.removeEventListener(
      'pointerenter',
      this.#handlePointerEnter
    );

    document.removeEventListener(
      'visibilitychange',
      this.#handleVisibilityChange
    );

    this.#scroll?.destroy();

    if (this.#resizeObserver) {
      this.#resizeObserver.disconnect();
    }
  }

  /**
   * Indica se este slideshow está dentro de outro.
   */
  get isNested() {
    return (
      this.parentElement?.closest(
        'slideshow-component'
      ) !== null
    );
  }

  get initialSlide() {
    return this.refs.slides?.[
      this.initialSlideIndex
    ];
  }

  /**
   * Seleciona um slide.
   */
  async select(
    input,
    event,
    options = {}
  ) {
    if (
      this.#disabled ||
      !this.refs.slides?.length
    ) {
      return;
    }

    for (const slide of this.refs.slides) {
      if (slide.hasAttribute('reveal')) {
        slide.removeAttribute('reveal');
        slide.setAttribute(
          'aria-hidden',
          'true'
        );
      }
    }

    let requestedIndex = (() => {
      if (typeof input === 'number') {
        return input;
      }

      if (typeof input === 'string') {
        return parseInt(input, 10);
      }

      if ('id' in input) {
        const requestedSlide =
          this.refs.slides.find(
            (slide) =>
              slide.getAttribute(
                'slide-id'
              ) == input.id
          );

        if (
          !requestedSlide ||
          !this.slides
        ) {
          return;
        }

        if (
          requestedSlide.hasAttribute(
            'hidden'
          )
        ) {
          requestedSlide.setAttribute(
            'reveal',
            ''
          );

          requestedSlide.setAttribute(
            'aria-hidden',
            'false'
          );

          /*
           * Resource lists nunca devem ficar inert.
           */
          if (
            requestedSlide.classList.contains(
              'resource-list__slide'
            )
          ) {
            requestedSlide.removeAttribute(
              'inert'
            );
          }
        }

        return this.slides.indexOf(
          requestedSlide
        );
      }
    })();

    const { current } = this;

    if (
      requestedIndex === undefined ||
      isNaN(requestedIndex) ||
      requestedIndex === current
    ) {
      return;
    }

    const { slides } = this;

    if (!slides?.length) return;

    if (!this.infinite) {
      requestedIndex = clamp(
        requestedIndex,
        0,
        slides.length - 1
      );
    }

    event?.preventDefault();

    const { animate = true } = options;
    const lastIndex =
      slides.length - 1;

    let index = requestedIndex;

    if (requestedIndex < 0) {
      index = lastIndex;
    } else if (
      requestedIndex > lastIndex
    ) {
      index = 0;
    }

    const isAdjacentSlide =
      Math.abs(index - current) <= 1 &&
      requestedIndex >= 0 &&
      requestedIndex <= lastIndex;

    const { visibleSlides } = this;

    const instant =
      prefersReducedMotion() ||
      !animate;

    if (
      !instant &&
      !isAdjacentSlide &&
      visibleSlides.length === 1
    ) {
      this.#disabled = true;

      await this.#scroll.finished;

      const targetSlide =
        slides[index];

      const currentSlide =
        slides[current];

      if (
        !targetSlide ||
        !currentSlide
      ) {
        this.#disabled = false;
        return;
      }

      const placeholder =
        document.createElement(
          'slideshow-slide'
        );

      targetSlide.before(
        placeholder
      );

      if (
        requestedIndex < current
      ) {
        currentSlide.before(
          targetSlide
        );
      } else {
        currentSlide.after(
          targetSlide
        );
      }

      if (current === 0) {
        this.#scroll.to(
          currentSlide,
          { instant: true }
        );
      }

      queueMicrotask(async () => {
        await this.#scroll.finished;

        this.#disabled = false;

        placeholder.replaceWith(
          targetSlide
        );

        this.#scroll.to(
          targetSlide,
          { instant: true }
        );
      });
    }

    const slide =
      slides[index];

    if (!slide) return;

    const previousIndex =
      this.current;

    slide.setAttribute(
      'aria-hidden',
      'false'
    );

    /*
     * Resource-list nunca deve receber inert.
     */
    if (
      slide.classList.contains(
        'resource-list__slide'
      )
    ) {
      slide.removeAttribute(
        'inert'
      );
    } else {
      slide.removeAttribute(
        'inert'
      );
    }

    this.#scroll.to(
      slide,
      { instant }
    );

    this.current =
      this.slides?.indexOf(
        slide
      ) || 0;

    this.#centerSelectedThumbnail(
      index,
      instant
        ? 'instant'
        : 'smooth'
    );

    this.dispatchEvent(
      new SlideshowSelectEvent({
        index,
        previousIndex,
        userInitiated:
          event != null,
        trigger: 'select',
        slide,
        id:
          slide.getAttribute(
            'slide-id'
          ),
      })
    );
  }

  /**
   * Avança.
   */
  next(event, options) {
    event?.preventDefault();

    this.select(
      this.nextIndex,
      event,
      options
    );
  }

  /**
   * Volta.
   */
  previous(event, options) {
    event?.preventDefault();

    this.select(
      this.previousIndex,
      event,
      options
    );
  }

  /**
   * Autoplay.
   */
  play(
    interval =
      this.autoplayInterval
  ) {
    if (this.#interval) return;

    this.paused = false;

    this.#interval =
      setInterval(() => {
        if (
          this.matches(':hover') ||
          document.hidden
        ) {
          return;
        }

        this.next();
      }, interval);
  }

  pause() {
    this.paused = true;
    this.suspend();
  }

  get paused() {
    return this.hasAttribute(
      'paused'
    );
  }

  set paused(value) {
    if (value) {
      this.setAttribute(
        'paused',
        ''
      );
    } else {
      this.removeAttribute(
        'paused'
      );
    }
  }

  suspend() {
    clearInterval(
      this.#interval
    );

    this.#interval =
      undefined;
  }

  resume() {
    if (
      !this.autoplay ||
      this.paused
    ) {
      return;
    }

    this.pause();
    this.play();
  }

  get autoplay() {
    return Boolean(
      this.autoplayInterval
    );
  }

  get autoplayInterval() {
    const interval =
      this.getAttribute(
        'autoplay'
      );

    const value =
      parseInt(
        `${interval}`,
        10
      );

    if (
      Number.isNaN(value)
    ) {
      return undefined;
    }

    return value * 1000;
  }

  #current = 0;

  get current() {
    return this.#current;
  }

  set current(value) {
    const {
      current,
      thumbnails,
      dots,
      slides,
      previous,
      next,
    } = this.refs;

    this.#current =
      value;

    if (current) {
      current.textContent =
        `${value + 1}`;
    }

    for (
      const controls
      of [thumbnails, dots]
    ) {
      controls?.forEach(
        (el, i) =>
          el.setAttribute(
            'aria-selected',
            `${i === value}`
          )
      );
    }

    if (previous) {
      previous.disabled =
        Boolean(
          !this.infinite &&
          value === 0
        );
    }

    if (next) {
      next.disabled =
        Boolean(
          !this.infinite &&
          slides &&
          this.nextIndex >=
            slides.length
        );
    }
  }

  get infinite() {
    return (
      this.getAttribute(
        'infinite'
      ) != null
    );
  }

  get visibleSlides() {
    return getVisibleElements(
      this.refs.scroller,
      this.slides,
      SLIDE_VISIBLITY_THRESHOLD,
      'x'
    );
  }

  get previousIndex() {
    const {
      current,
      visibleSlides,
    } = this;

    const modifier =
      visibleSlides.length > 1
        ? visibleSlides.length
        : 1;

    return (
      current - modifier
    );
  }

  get nextIndex() {
    const {
      current,
      visibleSlides,
    } = this;

    const modifier =
      visibleSlides.length > 1
        ? visibleSlides.length
        : 1;

    return (
      current + modifier
    );
  }

  get atStart() {
    const {
      current,
      slides,
    } = this;

    return slides?.length
      ? current === 0
      : false;
  }

  get atEnd() {
    const {
      current,
      slides,
    } = this;

    return slides?.length
      ? current ===
          slides.length - 1
      : false;
  }

  set disabled(value) {
    this.setAttribute(
      'disabled',
      String(value)
    );
  }

  get disabled() {
    return (
      this.getAttribute(
        'disabled'
      ) === 'true' ||
      (
        this.hasAttribute(
          'mobile-disabled'
        ) &&
        !mediaQueryLarge.matches
      )
    );
  }

  #disabled = false;

  #interval = undefined;

  #scroll;

  #resizeObserver;

  /**
   * Scroll do carrossel.
   */
  #handleScroll = () => {
    const previousIndex =
      this.#current;

    const index =
      this.#sync();

    if (
      index === previousIndex
    ) {
      return;
    }

    const slide =
      this.slides?.[index];

    if (!slide) return;

    this.dispatchEvent(
      new SlideshowSelectEvent({
        index,
        previousIndex,
        userInitiated: true,
        trigger: 'scroll',
        slide,
        id:
          slide.getAttribute(
            'slide-id'
          ),
      })
    );
  };

  #onTransitionInit = () => {
    this.setAttribute(
      'transitioning',
      ''
    );
  };

  #onTransitionEnd = () => {
    this.#updateVisibleSlides();

    this.removeAttribute(
      'transitioning'
    );
  };

  /**
   * Sincroniza posição atual.
   */
  #sync = () => {
    const { slides } = this;

    if (!slides) {
      return (
        this.current = 0
      );
    }

    const visibleSlides =
      this.visibleSlides;

    if (
      !visibleSlides.length
    ) {
      return this.current;
    }

    const { axis } =
      this.#scroll;

    const { scroller } =
      this.refs;

    const centers =
      visibleSlides.map(
        (slide) =>
          center(
            slide,
            axis
          )
      );

    const referencePoint =
      visibleSlides.length > 1
        ? scroller
            .getBoundingClientRect()[
              axis
            ]
        : center(
            scroller,
            axis
          );

    const closestCenter =
      closest(
        centers,
        referencePoint
      );

    const closestVisibleSlide =
      visibleSlides[
        centers.indexOf(
          closestCenter
        )
      ];

    if (
      !closestVisibleSlide
    ) {
      return (
        this.current = 0
      );
    }

    const index =
      slides.indexOf(
        closestVisibleSlide
      );

    return (
      this.current = index
    );
  };

  #dragging = false;

  #lastTouch = -Infinity;

  #markTouch = () => {
    this.#lastTouch = performance.now();
  };

  /**
   * Evita drag nativo de links e imagens.
   */
  #preventNativeDrag = (
    event
  ) => {
    if (
      !(
        event.target
        instanceof Element
      )
    ) {
      return;
    }

    const draggableContent =
      event.target.closest(
        'img, a'
      );

    if (
      draggableContent
    ) {
      event.preventDefault();
    }
  };

  /**
   * Drag com mouse.
   */
  #handleMouseDown = (
    event
  ) => {
    const { slides } = this;

    if (
      !slides ||
      slides.length <= 1
    ) {
      return;
    }

    if (
      !(
        event.target
        instanceof Element
      )
    ) {
      return;
    }

    if (
      this.disabled ||
      this.#dragging
    ) {
      return;
    }

    /*
     * Arraste com o dedo é feito pela rolagem nativa do navegador.
     * Este arraste manual é só para mouse.
     */
    if (
      event.sourceCapabilities?.firesTouchEvents ||
      performance.now() - this.#lastTouch < 1500
    ) {
      return;
    }

    /*
     * Não captura modelos 3D.
     */
    if (
      event.target.closest(
        'model-viewer'
      )
    ) {
      return;
    }

    /*
     * Elementos cuja interação deve continuar
     * funcionando normalmente.
     */
    if (
      event.target.closest(
        'button, input, select, textarea, label, summary'
      )
    ) {
      return;
    }

    const { axis } =
      this.#scroll;

    const startPosition =
      event[axis];

    const originalAnchor =
      event.target.closest(
        'a[href]'
      );

    const controller =
      new AbortController();

    const { signal } =
      controller;

    let previous =
      startPosition;

    let velocity = 0;

    let moved = false;

    let distanceTravelled =
      0;

    let lastTime =
      performance.now();

    this.#dragging = true;

    /*
     * Evita seleção de texto,
     * drag nativo de imagem/link etc.
     *
     * Se não houver movimento,
     * o clique será tratado manualmente.
     */
    event.preventDefault();

    const onPointerMove = (
      pointerEvent
    ) => {
      if (
        pointerEvent.pointerType &&
        pointerEvent.pointerType !== 'mouse'
      ) {
        return;
      }

      const current =
        pointerEvent[axis];

      const distanceFromStart =
        startPosition -
        current;

      /*
       * Pequena tolerância.
       */
      if (!moved) {
        if (
          Math.abs(
            distanceFromStart
          ) < 5
        ) {
          return;
        }

        moved = true;

        this.pause();

        this.setAttribute(
          'dragging',
          ''
        );

        this.#scroll.snap =
          false;

        try {
          this.setPointerCapture(
            pointerEvent.pointerId
          );
        } catch (_) {}
      }

      pointerEvent.preventDefault();

      pointerEvent.stopImmediatePropagation();

      const delta =
        previous - current;

      const now =
        performance.now();

      const timeDelta =
        Math.max(
          now - lastTime,
          1
        );

      velocity =
        (delta / timeDelta) *
        1000;

      previous =
        current;

      lastTime =
        now;

      distanceTravelled +=
        Math.abs(delta);

      this.#scroll.by(
        delta,
        {
          instant: true,
        }
      );
    };

    const navigateClick = () => {
      if (!originalAnchor) {
        return;
      }

      const href =
        originalAnchor.href;

      if (!href) return;

      /*
       * Ctrl / Cmd + clique.
       */
      if (
        event.metaKey ||
        event.ctrlKey
      ) {
        window.open(
          href,
          '_blank',
          'noopener'
        );

        return;
      }

      /*
       * Shift-click também abre
       * em uma nova janela.
       */
      if (event.shiftKey) {
        window.open(
          href,
          '_blank'
        );

        return;
      }

      window.location.assign(
        href
      );
    };

    const onPointerUp =
      async (
        pointerEvent
      ) => {
        controller.abort();

        this.#dragging =
          false;

        /*
         * CLIQUE NORMAL
         */
        if (!moved) {
          this.#scroll.snap =
            true;

          this.removeAttribute(
            'dragging'
          );

          this.resume();

          navigateClick();

          return;
        }

        /*
         * ARRASTE
         */
        const {
          current,
          slides,
        } = this;

        const {
          scroller,
        } = this.refs;

        if (
          !slides?.length ||
          !scroller
        ) {
          this.#scroll.snap =
            true;

          this.removeAttribute(
            'dragging'
          );

          this.resume();

          return;
        }

        const next =
          this.#sync();

        let modifier = 0;

        /*
         * Só força avanço/retorno
         * quando houve um gesto
         * minimamente intencional.
         */
        if (
          current === next &&
          distanceTravelled >=
            18 &&
          Math.abs(velocity) >=
            70
        ) {
          modifier =
            Math.sign(
              velocity
            );
        }

        const newIndex =
          clamp(
            next + modifier,
            0,
            slides.length - 1
          );

        const newSlide =
          slides[newIndex];

        if (!newSlide) {
          this.#scroll.snap =
            true;

          this.removeAttribute(
            'dragging'
          );

          this.resume();

          return;
        }

        const isMultiCardCarousel = Boolean(
  this.closest('.resource-list__carousel')
);

const maxScroll = Math.max(
  0,
  scroller.scrollWidth - scroller.clientWidth
);

const remainingScroll =
  maxScroll - scroller.scrollLeft;

if (isMultiCardCarousel && remainingScroll < newSlide.offsetWidth * 0.6) {
  scroller.scrollTo({
    left: maxScroll,
    behavior: prefersReducedMotion() ? 'auto' : 'smooth',
  });
} else {
  this.#scroll.to(newSlide);
}

        try {
          if (
            pointerEvent.pointerId !==
            undefined
          ) {
            this.releasePointerCapture(
              pointerEvent.pointerId
            );
          }
        } catch (_) {}

        this.#centerSelectedThumbnail(
          newIndex
        );

       this.current = newIndex;

await this.#scroll.finished;

if (this.#dragging) return;

this.#scroll.snap = true;

this.removeAttribute('dragging');

if (isMultiCardCarousel) {

  const maxScroll = Math.max(

    0,

    scroller.scrollWidth - scroller.clientWidth

  );

  if (maxScroll - scroller.scrollLeft < 8) {

    scroller.scrollLeft = maxScroll;

  }

}

this.resume();
      };

    document.addEventListener(
      'pointermove',
      onPointerMove,
      {
        signal,
        passive: false,
      }
    );

    document.addEventListener(
      'pointerup',
      onPointerUp,
      { signal }
    );

    document.addEventListener(
      'pointercancel',
      onPointerUp,
      { signal }
    );

    document.addEventListener(
      'pointercapturelost',
      onPointerUp,
      { signal }
    );
  };

  #handlePointerEnter = () => {
    this.setAttribute(
      'actioned',
      ''
    );
  };

  get slides() {
    return this.refs.slides?.filter(
      (slide) =>
        !slide.hasAttribute(
          'hidden'
        ) ||
        slide.hasAttribute(
          'reveal'
        )
    );
  }

  get initialSlideIndex() {
    const initialSlide =
      this.getAttribute(
        'initial-slide'
      );

    if (
      initialSlide == null
    ) {
      return 0;
    }

    return parseInt(
      initialSlide,
      10
    );
  }

  #handleVisibilityChange =
    () =>
      document.hidden
        ? this.pause()
        : this.resume();

  #updateControlsVisibility() {
    if (
      !this.hasAttribute(
        'auto-hide-controls'
      )
    ) {
      return;
    }

    const {
      scroller,
      slideshowControls,
    } = this.refs;

    if (
      !(
        slideshowControls
        instanceof HTMLElement
      )
    ) {
      return;
    }

    slideshowControls.hidden =
      scroller.scrollWidth <=
      scroller.offsetWidth;
  }

  #centerSelectedThumbnail(
    index,
    behavior = 'smooth'
  ) {
    const selectedThumbnail =
      this.refs.thumbnails?.[
        index
      ];

    if (!selectedThumbnail) {
      return;
    }

    const {
      thumbnailsContainer,
    } = this.refs;

    if (
      !thumbnailsContainer ||
      !(
        thumbnailsContainer
        instanceof HTMLElement
      )
    ) {
      return;
    }

    const {
      slideshowControls,
    } = this.refs;

    if (
      !slideshowControls ||
      !(
        slideshowControls
        instanceof HTMLElement
      )
    ) {
      return;
    }

    scrollIntoView(
      selectedThumbnail,
      {
        ancestor:
          thumbnailsContainer,
        behavior,
        block: 'center',
        inline: 'center',
      }
    );
  }

  /**
   * Atualiza aria-hidden e inert.
   *
   * Importante:
   * slides de listas de produtos continuam
   * interativos mesmo quando parcialmente fora
   * do viewport do carrossel.
   */
  #updateVisibleSlides() {
    const { slides } = this;

    if (
      !slides ||
      !slides.length
    ) {
      return 0;
    }

    const visibleSlides =
      this.visibleSlides;

    scheduler.schedule(() => {
      slides.forEach(
        (slide) => {
          const isVisible =
            visibleSlides.includes(
              slide
            );

          slide.setAttribute(
            'aria-hidden',
            `${!isVisible}`
          );

          /*
           * CARROSSEL DE PRODUTOS
           *
           * Nunca usa inert aqui.
           * Isso é essencial para não
           * matar o clique no último card.
           */
          if (
            slide.classList.contains(
              'resource-list__slide'
            )
          ) {
            slide.removeAttribute(
              'inert'
            );

            return;
          }

          /*
           * Sliders comuns mantêm
           * o comportamento acessível.
           */
          if (isVisible) {
            slide.removeAttribute(
              'inert'
            );
          } else {
            slide.setAttribute(
              'inert',
              ''
            );
          }
        }
      );
    });

    return visibleSlides.length;
  }
}

if (
  !customElements.get(
    'slideshow-component'
  )
) {
  customElements.define(
    'slideshow-component',
    Slideshow
  );
}